import { updateImageCanvas } from '@lidojs/design-core';
import { RootContent, RootContentProps } from '@lidojs/design-layers';
import React, {
  Fragment,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAsync } from 'react-use';
import { EditorContext } from '../editor/EditorContext';
import { useEditor, useLayer } from '../hooks';
import { LayerComponent } from '../types';
import { FrameLayerProps } from './FrameLayer';
import { ImageLayerProps } from './ImageLayer';

export interface RootLayerProps extends Omit<RootContentProps, 'image'> {
  image?: (RootContentProps['image'] & { thumb: string }) | null;
}

const RootLayer: LayerComponent<PropsWithChildren<RootLayerProps>> = ({
  layerId,
  boxSize,
  children,
  color,
  gradientBackground,
  image,
  video,
  position,
  rotate,
  scale,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { config, uploadImage } = useContext(EditorContext);
  const { actions } = useLayer();
  const { sidebar, isResizing, isDragging } = useEditor((state) => ({
    sidebar: state.sidebar,
    isResizing: state.resizeData.status,
    isDragging: state.dragData.status,
  }));
  const [isUploading, setIsUploading] = useState(false);
  const [imageSettings, setImageSettings] = useState<{
    brightness?: number | null;
    contrast?: number | null;
    saturation?: number | null;
    hueRotate?: number | null;
    grayscale?: number | null;
    blur?: number | null;
  }>({
    brightness: image?.brightness || null,
    contrast: image?.contrast || null,
    saturation: image?.saturation || null,
    hueRotate: image?.hueRotate || null,
    grayscale: image?.grayscale || null,
    blur: image?.blur || null,
  });
  const [imageEle, setImageEle] = useState<HTMLImageElement | null>(null);

  const showCanvas =
    image &&
    (imageSettings.brightness !== (image.brightness || null) ||
      imageSettings.contrast !== (image.contrast || null) ||
      imageSettings.saturation !== (image.saturation || null) ||
      imageSettings.hueRotate !== (image.hueRotate || null) ||
      imageSettings.grayscale !== (image.grayscale || null) ||
      imageSettings.blur !== (image.blur || null));

  useEffect(() => {
    if (image && !isResizing && !isDragging) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImageEle(img);
      };
      img.src = image.url;
    }
  }, [image, isDragging, isResizing]);

  useEffect(() => {
    const getImageSetting = () => {
      const imgRatio =
        config.frame.defaultImage.width / config.frame.defaultImage.height;
      const boxRatio = boxSize.width / boxSize.height;
      const w =
        imgRatio > boxRatio
          ? (boxSize.height / scale) * imgRatio
          : boxSize.width / scale;
      const h =
        imgRatio > boxRatio
          ? boxSize.height / scale
          : (boxSize.width / scale) * imgRatio;
      const res: FrameLayerProps['image'] = {
        boxSize: {
          width: w,
          height: h,
        },
        position: {
          x: -(w - boxSize.width / scale) / 2,
          y: -(h - boxSize.height / scale) / 2,
        },
        rotate: 0,
        url: config.frame.defaultImage.url,
        thumb: config.frame.defaultImage.url,
      };
      return res;
    };
  }, [
    image,
    color,
    gradientBackground,
    config.frame.defaultImage.width,
    config.frame.defaultImage.height,
    config.frame.defaultImage.url,
    boxSize.width,
    boxSize.height,
    scale,
  ]);

  const openEditor = () => {
    if (image || video) {
      actions.openImageEditor({
        boxSize,
        position,
        rotate,
        image,
        video,
      });
    }
  };

  useEffect(() => {
    if (
      !sidebar &&
      !isUploading &&
      image &&
      (imageSettings.brightness !== (image.brightness || null) ||
        imageSettings.contrast !== (image.contrast || null) ||
        imageSettings.saturation !== (image.saturation || null) ||
        imageSettings.hueRotate !== (image.hueRotate || null) ||
        imageSettings.grayscale !== (image.grayscale || null) ||
        imageSettings.blur !== (image.blur || null))
    ) {
      setIsUploading(true);
      const data = {
        brightness: image.brightness || null,
        contrast: image.contrast || null,
        saturation: image.saturation || null,
        hueRotate: image.hueRotate || null,
        grayscale: image.grayscale || null,
        blur: image.blur || null,
      };
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.toBlob(async (blob) => {
          const file = new File([blob as Blob], 'file.png', {
            type: 'image/png',
          });
          const { url, thumb } = await uploadImage(file);
          const img = new Image();
          img.src = url;
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            // avoid flicker
            setImageSettings(data);
            setIsUploading(false);
            actions.setProp<ImageLayerProps>({
              image: {
                url,
                thumb,
                original: image.original ?? image.url,
              },
            });
          };
        }, 'image/png');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actions,
    image?.blur,
    image?.brightness,
    image?.contrast,
    image?.original,
    image?.saturation,
    image?.hueRotate,
    image?.grayscale,
    image?.url,
    imageSettings.blur,
    imageSettings.brightness,
    imageSettings.contrast,
    imageSettings.saturation,
    imageSettings.grayscale,
    imageSettings.hueRotate,
    isUploading,
    sidebar,
    uploadImage,
  ]);

  useAsync(async () => {
    if (
      canvasRef.current &&
      !isResizing &&
      !isDragging &&
      showCanvas &&
      imageEle &&
      image
    ) {
      await updateImageCanvas(
        canvasRef.current,
        image,
        imageEle,
        image.boxSize
      );
    }
  }, [isResizing, isDragging, image]);

  const wrapperStyle = useMemo(
    () => ({
      transform:
        !!image?.flipVertical || !!image?.flipHorizontal
          ? `scale(${image?.flipHorizontal ? -1 : 1}, ${
              image?.flipVertical ? -1 : 1
            })`
          : undefined,
      width: boxSize.width,
      height: boxSize.height,
    }),
    [image?.flipVertical, image?.flipHorizontal, boxSize.width, boxSize.height]
  );
  return (
    <Fragment>
      <RootContent
        boxSize={boxSize}
        color={color}
        gradientBackground={gradientBackground}
        image={image}
        layerId={layerId}
        position={position}
        rotate={rotate}
        scale={scale}
        video={video}
        onDoubleClick={openEditor}
      >
        {image && (
          <>
            <div css={wrapperStyle}>
              <img
                alt={image.url}
                css={(theme) => ({
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill',
                  position: 'absolute',
                  pointerEvents: 'none',
                  visibility: showCanvas ? 'hidden' : 'visible',
                })}
                src={image.url}
              />
            </div>

            <div
              css={(theme) => ({
                position: 'absolute',
                inset: 0,
                visibility: !showCanvas || !image ? 'hidden' : 'visible',
              })}
            >
              <div
                css={(theme) => ({
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'auto',
                })}
              >
                <canvas ref={canvasRef} />
              </div>
            </div>
          </>
        )}
      </RootContent>
      {children}
    </Fragment>
  );
};

RootLayer.info = {
  name: 'Main',
  type: 'Root',
};
export default RootLayer;
